/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHeader,
    IList,
    IOrderHistoryResources,
    IOrderHistoryViewProps,
    IOrderInformation,
    ISalesOrder
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation & { resources: IOrderHistoryResources }> = ({
    orderInformationProps,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    channelReferenceId,
    resources
}) => (
    <Node {...orderInformationProps}>
        {/* {channelName} */}
        <span className='ms-order-history__order-information-label'>
            <span className='ms-order-history__order-information-key'>{`${resources.orderHistoryCreatedDateText}: `}</span>
            {createdDate}
        </span>
        <span className='ms-order-history__order-information-label'>
            <span className='ms-order-history__order-information-key'>{`${resources.orderHistoryOrderNumberText}: `}</span>
            {salesId}
        </span>
        {receiptId}
        {/* {channelReferenceId} */}
        {/* {count} */}
        <span className='ms-order-history__order-information-label'>
            <span className='ms-order-history__order-information-key'>{`${resources.orderHistoryTotalText}: `}</span>
            {amount}
        </span>
    </Node>
);

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

export const OrderHistoryGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <a href={salesLine.data.productUrl} aria-label={salesLine.data.product?.Name}>
                        <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                    </a>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader & { resources: IOrderHistoryResources }> = ({
    headerProps,
    heading,
    orderCountLabel,
    extraActions,
    resources
}) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);

export const OrderHistorySalesOder: React.FC<ISalesOrder & { resources: IOrderHistoryResources }> = ({
    salesOrderProps,
    orderInfomation,
    groups,
    orderDetailsLink,
    expandProductsButton,
    resources
}) => (
    <Node {...salesOrderProps}>
        <div className='ms-order-history__sales-order-information'>
            {orderInfomation && <OrderHistoryOrderInfomation {...{ ...orderInfomation, resources }} />}
            {orderDetailsLink}
        </div>
        {/*  <div className='ms-order-history__sales-order-information-lines'>
            {orderInfomation.count}
            {groups && <OrderHistoryGroups {...groups} />}
            {expandProductsButton}
        </div> */}
    </Node>
);

export const OrderHistoryList: React.FC<IList & { resources: IOrderHistoryResources }> = ({ listProps, salesOrders, resources }) => (
    <Node {...listProps}>
        {salesOrders && salesOrders.map((salesOrder, index) => <OrderHistorySalesOder key={index} {...{ ...salesOrder, resources }} />)}
    </Node>
);

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
    orderHistoryProps,
    header,
    alert,
    loading,
    emptyMessage,
    backToShoppingLink,
    list,
    table,
    moreButton,
    ...props
}) => {
    const resources = props.resources as IOrderHistoryResources;

    return (
        <Module {...orderHistoryProps}>
            {header && <OrderHistoryHeader {...{ ...header, resources }} />}
            {loading}
            {alert && (
                <>
                    {alert}
                    {backToShoppingLink}
                </>
            )}
            {emptyMessage && (
                <Node className='ms-order-history__empty-message-container'>
                    {emptyMessage}
                    {backToShoppingLink}
                </Node>
            )}
            {list && <OrderHistoryList {...{ ...list, resources }} />}
            {table}
            {moreButton && moreButton}
        </Module>
    );
};

export default OrderHistoryView;
